import { MemberRoleEnum, PermissionsEnum } from '@novu/shared';
import React, { useEffect, useMemo, useState } from 'react';
import { createContextHook } from '../context';
import { DecodedJwt } from '.';
import { decodeJwtPayload, getJwtToken, isJwtValid, subscribeToJwtTokenChange } from './jwt-manager';
import { createUserFromJwt, SelfHostedUser } from './user.types';

export type SelfHostedAuthContextValue = {
  currentUser: SelfHostedUser | null;
  has: (params: { permission: PermissionsEnum } | { role: MemberRoleEnum }) => boolean;
};

const defaultAuthContextValue: SelfHostedAuthContextValue = {
  currentUser: null,
  has: () => true,
};

export const AuthContext = React.createContext<SelfHostedAuthContextValue>(defaultAuthContextValue);

export function AuthContextProvider({ children }: any) {
  const [jwt, setJwt] = useState<string | null>(() => getJwtToken());

  useEffect(() => {
    setJwt(getJwtToken());

    return subscribeToJwtTokenChange(() => {
      setJwt(getJwtToken());
    });
  }, []);

  const decodedJwt = useMemo<DecodedJwt | null>(
    () => (isJwtValid(jwt) ? decodeJwtPayload<DecodedJwt>(jwt) : null),
    [jwt]
  );

  const value = useMemo(
    () => ({
      currentUser: createUserFromJwt(decodedJwt),
      has: () => true,
    }),
    [decodedJwt]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuthContext = createContextHook(AuthContext);
